import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { OrgAlert } from '@/components/common/OrgAlert'
import { useOrgs } from '@/contexts/OrgsContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { useGetAgents } from '@/api/control/control-plane/control-plane'

/**
 * Dashboard alert card listing offline agents, whose profiles are paused until they are back online.
 * Returns null when every agent is online.
 */
export function AgentOfflineAlert() {
  const { selectedOrganizationId } = useOrgs()
  const { t } = useLanguage()

  const { data: agentsResponse } = useGetAgents(selectedOrganizationId ?? '', {
    query: {
      enabled: !!selectedOrganizationId,
    },
  })

  const offlineAgents = useMemo(
    () => (agentsResponse?.data ?? []).filter((agent) => agent.healthState === 'Offline'),
    [agentsResponse],
  )

  if (offlineAgents.length === 0) {
    return null
  }

  return (
    <Card className="flex-shrink-0 w-fit min-w-[350px]">
      <CardHeader className="pb-3">
        <CardTitle>{t.agents.agentOffline.title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col sm:flex-row gap-3">
        <OrgAlert variant="error" title={t.agents.agentOffline.title}>
          <p className="text-xs">
            {t.agents.agentOffline.dashboardDescription.replace('{count}', String(offlineAgents.length))}
          </p>
          <ul className="text-xs list-disc list-inside">
            {offlineAgents.map((agent) => (
              <li key={agent.id}>
                <strong>{agent.name}</strong>
                {agent.offlineSinceUtc
                  ? ` – ${t.agents.health.offlineSince.replace('{time}', new Date(agent.offlineSinceUtc).toLocaleString())}`
                  : null}
              </li>
            ))}
          </ul>
          <div className="mt-auto self-end">
            <Button asChild size="sm" variant="outline">
              <Link to={`/dashboard/orgs/${selectedOrganizationId ?? ''}/agents`}>
                {t.agents.agentOffline.viewAgents}
                <ArrowRight className="ml-2 h-3 w-3" />
              </Link>
            </Button>
          </div>
        </OrgAlert>
      </CardContent>
    </Card>
  )
}
